if (!customElements.get("image-marks")) {
  class ImageMarks extends HTMLElement {
    constructor() {
      super();
      try {
        this.marksPosition = this.getAttribute("marks-position")
          ?.split(";")
          .reduce((acc, cur) => {
            try {
              const [id, position] = cur.split(":");
              const [x, y] = position.split("-");
              acc[id] = { percentX: Number(x) / 100, percentY: Number(y) / 100 };
              return acc;
            } catch {
              return acc;
            }
          }, {});
        this.marks = {};
        this.lineStyle = this.getAttribute("line-style") || "solid";
      } catch (e) {
        console.error(e);
      }
    }

    connectedCallback() {
      this.renderAllLines();
      if (!this._resizeObserver) {
        this._resizeObserver = new ResizeObserver(() => {
          this.renderAllLines();
        });
        this._resizeObserver.observe(this);
      }
    }

    setDefaultStyle() {
      this.style.position = "absolute";
      this.style.width = "100%";
      this.style.height = "100%";
      this.style.top = 0;
      this.style.left = 0;
      Array.from(this.children).forEach((child) => {
        const id = child.id;
        if (this.marks[id]) {
          child.style.position = "absolute";
          child.style.transform = ["left", "right"].includes(this.marks[id].side)
            ? "translateY(-50%)"
            : "translateX(-50%)";
        }
      });
    }

    renderAllLines() {
      try {
        const prevs = this.querySelectorAll(".svg-line-overlay");
        prevs.forEach((n) => n.remove());

        this.getChildPosition();

        this.setDefaultStyle();

        Object.keys(this.marks).forEach((markId) => {
          const { x: startX, y: startY } = this.getMarkSide(this.marks[markId]);
          const { x: endX, y: endY } = this.getEndSide(this.marks[markId]);
          this.renderLine(markId, startX, startY, endX, endY);
        });
      } catch (e) {
        console.error(e);
      }
    }

    getChildPosition() {
      Array.from(this.children).forEach((child) => {
        const id = child.id;
        if (id) {
          this.marks[id] = {
            id: id,
            side: child.dataset.side || "left",
            offset: {
              left: child.offsetLeft,
              top: child.offsetTop,
              width: child.offsetWidth,
              height: child.offsetHeight,
            },
          };
        }
      });
    }

    getMarkSide(mark) {
      const side = mark.side;
      const { left, top, width, height } = mark.offset;
      switch (side) {
        case "left":
          return {
            x: left,
            y: top,
          };
        case "right":
          return {
            x: left + width,
            y: top,
          };
        case "bottom":
          return {
            x: left,
            y: top + height,
          };
        default:
          return {
            x: left,
            y: top,
          };
      }
    }

    getEndSide(mark) {
      const { percentX, percentY } = this.marksPosition[mark.id];
      const containerWidth = this.offsetWidth;
      const conainerHeight = this.offsetHeight;
      return {
        x: containerWidth * percentX,
        y: conainerHeight * percentY,
      };
    }

    renderLine(markId, startX, startY, endX, endY) {
      // visual config
      const currentColor = "currentColor";
      const strokeWidth = 1;
      const padding = 4; // padding so stroke isn't clipped
      const circleRadius = Number(getComputedStyle(this).getPropertyValue('--dot-radius')) || 0;

      // compute bounding box that contains both points (plus padding)
      const minX = Math.min(startX, endX) - padding;
      const minY = Math.min(startY, endY) - padding;
      const maxX = Math.max(startX, endX) + padding;
      const maxY = Math.max(startY, endY) + padding;
      const width = Math.max(1, maxX - minX);
      const height = Math.max(1, maxY - minY);

      // create SVG element positioned absolutely inside this element
      const SVG_NS = "http://www.w3.org/2000/svg";
      const svg = document.createElementNS(SVG_NS, "svg");
      svg.setAttribute("class", "svg-line-overlay");
      svg.id = `${markId}-line`;
      // place the svg at the bounding box position so we can use local coordinates
      svg.style.position = "absolute";
      svg.style.left = `${minX}px`;
      svg.style.top = `${minY}px`;
      svg.style.width = `${width}px`;
      svg.style.height = `${height}px`;
      svg.style.pointerEvents = "none"; // let clicks pass through
      svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
      svg.setAttribute("preserveAspectRatio", "none");

      // helper to create a line with coordinates in the local svg space
      const makeLine = (x1, y1, x2, y2) => {
        const line = document.createElementNS(SVG_NS, "line");
        line.setAttribute("x1", String(x1));
        line.setAttribute("y1", String(y1));
        line.setAttribute("x2", String(x2));
        line.setAttribute("y2", String(y2));
        line.setAttribute("stroke", currentColor);
        line.setAttribute("stroke-width", String(strokeWidth));
        line.setAttribute("stroke-linecap", "butt");

        if (this.lineStyle === "dashed") {
          line.setAttribute("stroke-dasharray", "12");
        }
        return line;
      };

      const makeDot = (cx, cy, direction) => {
        const circle = document.createElementNS(SVG_NS, "circle");
        circle.setAttribute("cx", String(cx));
        circle.setAttribute("cy", String(cy));
        circle.setAttribute("r", String(circleRadius));
        circle.setAttribute("fill", currentColor);
        circle.setAttribute("class", `dot-${direction}`)
        return circle;
      };

      // convert global coordinates -> svg-local coordinates
      const lx = (x) => x - minX;
      const ly = (y) => y - minY;

      if (startX === endX) {
        // single vertical line
        const line = makeLine(lx(startX), ly(startY), lx(endX), ly(endY));
        svg.appendChild(line);
      } else if (startY === endY) {
        // single horizontal line
        const line = makeLine(lx(startX), ly(startY), lx(endX), ly(endY));
        svg.appendChild(line);
      } else {
        // two-segment elbow: horizontal from start -> (endX, startY), then vertical to end
        // (choice: elbow at (endX, startY))
        const midX = endX;
        const midY = startY;

        const hLine = makeLine(lx(startX), ly(startY), lx(midX), ly(midY));
        const vLine = makeLine(lx(midX), ly(midY), lx(endX), ly(endY));

        svg.appendChild(hLine);
        svg.appendChild(vLine);
      }

      const endDot = makeDot(lx(endX), ly(endY), 'end');
      svg.appendChild(endDot);
      const startDot = makeDot(lx(startX), ly(startY), 'start');
      svg.appendChild(startDot);
      // append svg to this component
      this.appendChild(svg);

      // return svg element for further manipulation if needed
      return svg;
    }
  }
  customElements.define("image-marks", ImageMarks);
}
